/**
 * The graph's detail panel — the entry, shown beside the graph.
 *
 * Clicking a node used to navigate to /kb/:id, which threw away the thing the
 * click was asking about: you lost the graph, the selection, and your place in
 * it, to read three lines of body text. The panel answers the question without
 * leaving the view, and the full entry is still one link away.
 *
 * Pure input→HTML, like the rest of this view's rendering: state lives in
 * kb_graph.ts.
 *
 * CSS: styles/views/kb-graph.css
 */

import { esc, renderMd } from '../../shared/rendering.js'
import { action } from '../components/action-delegation.js'
import { kbTypeColor } from './kb_types.js'
import type { KBEntryDetail } from '../../types/api-secondary.js'

/** A neighbour as the graph knows it — the panel never re-fetches these. */
export type PanelNeighbour = {
  readonly id: string
  readonly title: string
  readonly type: string
  readonly kind: 'relation' | 'tag'
  /** Tags that produced a derived edge, strongest first. */
  readonly via: readonly string[]
}

export type PanelState = {
  readonly selectedId: string | null
  readonly entry: KBEntryDetail | null
  readonly loading: boolean
  readonly error: boolean
  readonly neighbours: readonly PanelNeighbour[]
}

function renderNeighbour(n: PanelNeighbour): string {
  // Why an edge exists is the question the graph provokes and cannot answer on
  // its own — a line between two dots is not a reason. The shared tags are it.
  const why =
    n.kind === 'relation'
      ? `<span class="kg-why kg-why-drawn">linked by hand</span>`
      : n.via.length > 0
        ? `<span class="kg-why">${n.via.map((t) => esc(t)).join(' · ')}</span>`
        : ''
  return `<li class="kg-nb">
    <button class="kg-nb-btn" ${action('selectGraphNode', n.id)}>
      <i class="kg-nb-dot" style="background:var(--${kbTypeColor(n.type)})"></i>
      <span class="kg-nb-title">${esc(n.title)}</span>
    </button>
    ${why}
  </li>`
}

function renderNeighbours(neighbours: readonly PanelNeighbour[]): string {
  if (neighbours.length === 0) {
    // Not an error, and worth saying outright: it is the same finding the
    // isolated count in the header reports, seen from one entry's point of view.
    return `<div class="kg-p-section">
      <h3 class="kg-p-h">Connections</h3>
      <p class="kg-p-none">Nothing links to this entry. It shares no rare tag with
        anything else in the project, and no relation has been drawn to it by hand.</p>
    </div>`
  }
  const drawn = neighbours.filter((n) => n.kind === 'relation').length
  const note = drawn > 0 ? ` · ${String(drawn)} drawn by hand` : ''
  return `<div class="kg-p-section">
    <h3 class="kg-p-h">Connections <span class="kg-p-count">${String(neighbours.length)}${esc(note)}</span></h3>
    <ul class="kg-nbs">${neighbours.map((n) => renderNeighbour(n)).join('')}</ul>
  </div>`
}

/** The panel. Returns '' when nothing is selected, so the caller can just inline it. */
export function renderGraphPanel(state: PanelState): string {
  if (!state.selectedId) return ''

  const close = `<button class="kg-p-close" ${action('closeGraphPanel', '')} aria-label="Close entry details">×</button>`
  const shell = (inner: string): string =>
    `<aside class="kg-panel" role="complementary" aria-label="Entry details">${close}${inner}</aside>`

  if (state.loading) {
    return shell(`<div class="loading"><div class="spinner"></div></div>`)
  }
  if (state.error || !state.entry) {
    return shell(`<div class="empty">Could not load this entry.</div>`)
  }

  const e = state.entry
  const tags =
    e.tags.length > 0
      ? `<div class="kg-p-tags">${e.tags.map((t) => `<span class="kg-p-tag">${esc(t)}</span>`).join('')}</div>`
      : ''

  // The body is the entry's own markdown, rendered through the shared
  // marked + DOMPurify path — never interpolated raw.
  const body = e.body
    ? `<div class="kg-p-body md">${renderMd(e.body)}</div>`
    : `<p class="kg-p-none">This entry has no body.</p>`

  return shell(`
    <header class="kg-p-head">
      <span class="kg-p-type" style="color:var(--${kbTypeColor(e.type)})">${esc(e.type)}</span>
      <h2 class="kg-p-title">${esc(e.title)}</h2>
      <code class="kg-p-id">${esc(e.id)}</code>
    </header>
    ${tags}
    ${renderNeighbours(state.neighbours)}
    <div class="kg-p-section">
      <h3 class="kg-p-h">Entry</h3>
      ${body}
    </div>
    <a class="kg-p-open" href="#/kb/${encodeURIComponent(e.id)}">Open the full entry →</a>
  `)
}
